import api, { APIResponse } from './api';
import Order, { OrderInterface } from './order';
import { load as loadRecaptcha } from 'recaptcha-v3';

export enum NotificationChannel {
    SMS_ONLY = 'sms',
    EMAIL_ONLY = 'email',
    EMAIL_SMS = 'email-sms',
}

export interface UserInterface {
    id?: number;
    name: string;
    email: string;
    phone: string;
    readonly provider?: string;
    notify_by: NotificationChannel;
    default_address: string;
    business_name: string;
    delivery_instructions: string;
    address_place_id: string;
    fcm_token: string | null;
    membership_number?: string;
}

export interface Driver {
    id?: number;
    name: string;
    email: string;
    phone: string;
}
export default class User implements UserInterface {
    [key: string]: any;
    id?: number;
    name: string;
    email: string;
    phone: string;
    readonly provider?: string;
    notify_by: NotificationChannel;
    default_address: string;
    business_name: string;
    delivery_instructions: string;
    address_place_id: string;
    fcm_token: string | null;
    membership_number?: string;

    constructor(user: UserInterface) {
        this.id = user.id;
        this.name = user.name;
        this.email = user.email;
        this.notify_by = user.notify_by;
        this.default_address = user.default_address;
        this.business_name = user.business_name;
        this.delivery_instructions = user.delivery_instructions;
        this.address_place_id = user.address_place_id;
        this.phone = user.phone;
        this.fcm_token = user.fcm_token || '';
        if (user.membership_number !== undefined) this.membership_number = user.membership_number;
        if (user.provider !== undefined) this.provider = user.provider;
    }

    public async update(): Promise<boolean> {
        const response = await api.post<null>(
            { name: 'user.update', parameters: { id: this.id } },
            {
                name: this.name,
                email: this.email,
                phone: this.phone,
                notify_by: this.notify_by,
                default_address: this.default_address,
                address_place_id: this.address_place_id,
                business_name: this.business_name,
                delivery_instructions: this.delivery_instructions,
                fcm_token: this.fcm_token,
                membership_number: this.membership_number,
            }
        );
        return response.success;
    }

    public async requestVerificationCode(): Promise<APIResponse<null>> {
        const recaptcha = await loadRecaptcha((<any>window).captchaKey, { autoHideBadge: true, useRecaptchaNet: true });
        const token = await recaptcha.execute('request_verification_token');
        const response = await api.post<null>(
            { name: 'user.request_otp' },
            {
                captcha_response: token,
                phone: this.phone,
                name: this.name,
            }
        );
        return response;
    }

    public async save(password: string, otpCode: string | null): Promise<APIResponse<null>> {
        const recaptcha = await loadRecaptcha((<any>window).captchaKey, { autoHideBadge: true, useRecaptchaNet: true });
        const token = await recaptcha.execute('create_user');
        const response = await api.post<null>(
            { name: 'user.create' },
            {
                captcha_response: token,
                name: this.name,
                email: this.email,
                phone: this.phone,
                password: password,
                default_address: this.default_address,
                address_place_id: this.address_place_id,
                otp_token: otpCode,
            }
        );
        return response;
    }

    public async changePassword(currentPassword: string, newPassword: string): Promise<boolean> {
        const response = await api.post<null>(
            { name: 'user.password' },
            {
                current: currentPassword,
                new: newPassword,
            }
        );
        return response.success;
    }

    public async orders(): Promise<Order[]> {
        const response = await api.get<OrderInterface[]>({ name: 'user.orders', parameters: { id: this.id } });
        if (response.success) {
            return Order.createBatch(<OrderInterface[]>response.content);
        }
        return [];
    }

    public async activeOrders(): Promise<Order[]> {
        const response = await api.get<OrderInterface[]>({ name: 'user.active_orders', parameters: { id: this.id } });
        if (response.success) {
            return Order.createBatch(<OrderInterface[]>response.content);
        }
        return [];
    }

    static async login(email: string, password: string): Promise<User | null> {
        const response = await api.post<UserInterface | null>({ name: 'user.login' }, { email, password });
        if (response.success) {
            return new User(response.content as UserInterface);
        }
        return null;
    }

    static async resetPassword(token: string, email: string, password: string, passwordConfirmation: string): Promise<string> {
        const recaptcha = await loadRecaptcha((<any>window).captchaKey, { autoHideBadge: true, useRecaptchaNet: true });
        const captcha_response = await recaptcha.execute('create_user');
        const response = await api.post<string>(
            { name: 'user.password.reset' },
            {
                captcha_response,
                email,
                token,
                password,
                password_confirmation: passwordConfirmation,
            }
        );
        if (response.success) {
            return response.content as string;
        }
        return '';
    }

    static async lostPassword(email: string): Promise<boolean> {
        const recaptcha = await loadRecaptcha((<any>window).captchaKey, { autoHideBadge: true, useRecaptchaNet: true });
        const captcha_response = await recaptcha.execute('reset_password');
        const response = await api.post<null>({ name: 'user.password.lost' }, { captcha_response, email });
        return response.success;
    }

    static async exists(email: string): Promise<APIResponse<{ has_provider: boolean }>> {
        const response = await api.get<{ has_provider: boolean }>({ name: 'user.exists', parameters: { email } });
        return response;
    }

    static async emailVerified(email: string, password: string): Promise<boolean> {
        const response = await api.get<{ success: boolean }>({ name: 'user.verified', parameters: { email, password } });
        return response.success;
    }

    public async isValidMember(token: string): Promise<boolean> {
        const response = await api.get<{ success: boolean }>({ name: 'membership.exists', parameters: { token } });
        return response.success;
    }

    public async requestEmailVerification(): Promise<APIResponse<null>> {
        const response = await api.post<null>({ name: 'verification.send' });
        return response;
    }

    static async find(id: number): Promise<User | null> {
        const response = await api.get<UserInterface>({ name: 'user.show', parameters: { id } });
        if (response.success) {
            return new User(response.content as UserInterface);
        }
        return null;
    }

    static async current(): Promise<User | null> {
        const response = await api.get<UserInterface>({ name: 'user.current' });
        if (response.success) {
            return new User(response.content as UserInterface);
        }
        return null;
    }

    static async drivers(): Promise<Driver[]> {
        const response = await api.get<Driver[]>({ name: 'user.drivers' });
        if (response.success) {
            return response.content as Driver[];
        }
        return [];
    }
}
